/**
 * 用户日志查询 API 服务
 * 用于获取用户的充值和消费日志
 */

const API_BASE_URL = "/api";

/**
 * 获取认证 token
 */
function getAuthHeaders(): HeadersInit {
  const authStore = localStorage.getItem("auth-storage");
  if (authStore) {
    const { state } = JSON.parse(authStore);
    const token = state?.accessToken;
    if (token) {
      return {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };
    }
  }
  return {
    "Content-Type": "application/json",
  };
}

/**
 * 日志条目
 */
export interface LogEntry {
  type: number;
  typeName: string;
  content: string;
  modelName: string | null;
  quota: number;
  promptTokens: number;
  completionTokens: number;
  createdAt: string | number;
  createdTimestamp: number;
}

/**
 * 日志查询参数
 */
export interface LogQueryParams {
  page?: number;
  type?: number;
  tokenName?: string;
  modelName?: string;
  startTimestamp?: number;
  endTimestamp?: number;
}

/**
 * 日志查询响应
 */
export interface LogQueryResponse {
  success: boolean;
  data: {
    logs: LogEntry[];
    page: number;
    type: number;
  };
  message: string;
}

/**
 * 日志统计响应
 */
export interface LogStatsResponse {
  success: boolean;
  data: {
    totalQuota: number;
    type: number;
    startTimestamp: number;
    endTimestamp: number;
  };
  message: string;
}

/**
 * 获取用户操作日志
 */
export async function getUserLogs(
  params: LogQueryParams = {}
): Promise<LogQueryResponse> {
  const queryParams = new URLSearchParams();

  if (params.page !== undefined) {
    queryParams.set("page", String(params.page));
  }
  if (params.type !== undefined) {
    queryParams.set("type", String(params.type));
  }
  if (params.tokenName) {
    queryParams.set("tokenName", params.tokenName);
  }
  if (params.modelName) {
    queryParams.set("modelName", params.modelName);
  }
  if (params.startTimestamp !== undefined) {
    queryParams.set("startTimestamp", String(params.startTimestamp));
  }
  if (params.endTimestamp !== undefined) {
    queryParams.set("endTimestamp", String(params.endTimestamp));
  }

  const queryString = queryParams.toString();
  const url = `${API_BASE_URL}/user/logs${queryString ? `?${queryString}` : ""}`;

  const response = await fetch(url, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "获取日志失败");
  }

  return response.json();
}

/**
 * 获取用户日志统计
 */
export async function getUserLogStats(
  params: LogQueryParams = {}
): Promise<LogStatsResponse> {
  const queryParams = new URLSearchParams();

  if (params.type !== undefined) {
    queryParams.set("type", String(params.type));
  }
  if (params.tokenName) {
    queryParams.set("tokenName", params.tokenName);
  }
  if (params.modelName) {
    queryParams.set("modelName", params.modelName);
  }
  if (params.startTimestamp !== undefined) {
    queryParams.set("startTimestamp", String(params.startTimestamp));
  }
  if (params.endTimestamp !== undefined) {
    queryParams.set("endTimestamp", String(params.endTimestamp));
  }

  const queryString = queryParams.toString();
  const url = `${API_BASE_URL}/user/logs/stats${queryString ? `?${queryString}` : ""}`;

  const response = await fetch(url, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "获取日志统计失败");
  }

  return response.json();
}
